import { memo, ReactElement, ReactNode } from 'react'
import { Box, Divider } from '@mui/material'
import SubtleShimmer from './SubtleShimmer'

export type ListSkeletonVariant = 'positions' | 'orders' | 'holdings' | 'news'

type Props = {
    variant: ListSkeletonVariant
    /** How many placeholder rows to draw. Defaults per variant. */
    rows?: number
    /** Stretch to the parent's height (sidebar panes). Off for inline "load more" placeholders. */
    fill?: boolean
}

const DEFAULT_ROWS: Record<ListSkeletonVariant, number> = { positions: 8, orders: 6, holdings: 6, news: 5 }

// Bare boxes with sizes only: SubtleShimmer measures them and paints the flat placeholder fills.
const Bar = ({ w, h = 12 }: { w: number | string; h?: number }) => (
    <Box sx={{ width: w, height: h, borderRadius: 0.5, flexShrink: 0 }} />
)
const Space = () => <Box sx={{ flex: 1 }} />
const Line = ({ children, mt = 0 }: { children: ReactNode; mt?: number }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt }}>{children}</Box>
)

const PositionsRow = () => (
    <Box sx={{ px: 1, py: 1.25 }}>
        <Line>
            <Bar w="34%" />
            <Space />
            <Bar w={28} />
            <Bar w={44} />
            <Bar w={44} />
            <Bar w={48} />
        </Line>
    </Box>
)

const OrdersRow = () => (
    <Box sx={{ px: 1, py: 1 }}>
        <Line>
            <Bar w="40%" />
            <Bar w={14} />
            <Space />
            <Bar w={36} />
            <Bar w={40} />
            <Bar w={34} />
        </Line>
        <Line mt={0.75}>
            <Bar w={30} h={10} />
            <Bar w={26} h={10} />
            <Bar w={34} h={10} />
            <Bar w={30} h={10} />
        </Line>
    </Box>
)

const HoldingsRow = () => (
    <Box sx={{ px: 1.5, py: 1.25 }}>
        <Line>
            <Bar w="40%" />
            <Space />
            <Bar w={48} />
        </Line>
        <Line mt={0.75}>
            <Bar w="30%" h={10} />
            <Space />
            <Bar w={40} h={10} />
        </Line>
    </Box>
)

/** Mirrors HoldingsHeader: four summary stats in two columns above the list. */
const HoldingsSummary = () => (
    <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1.5 }}>
        {Array.from({ length: 4 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                <Bar w={52} h={10} />
                <Bar w={72} h={14} />
            </Box>
        ))}
    </Box>
)

const NewsRow = () => (
    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Bar w="100%" h={14} />
        <Bar w="72%" h={14} />
        <Bar w="100%" h={11} />
        <Bar w="88%" h={11} />
        <Line mt={0.25}>
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0 }} />
            <Bar w={96} h={10} />
        </Line>
    </Box>
)

const ROW: Record<ListSkeletonVariant, () => ReactElement> = {
    positions: PositionsRow,
    orders: OrdersRow,
    holdings: HoldingsRow,
    news: NewsRow
}

/**
 * Loading placeholder for the list-style tabs (positions, orders, holdings, news).
 * Same idea as AlertsListSkeleton: structure only, painted by SubtleShimmer.
 */
const ListSkeleton = ({ variant, rows = DEFAULT_ROWS[variant], fill = true }: Props) => {
    const Row = ROW[variant]
    return (
        <SubtleShimmer loading fill={fill}>
            <Box sx={{ width: '100%' }}>
                {variant === 'holdings' && <HoldingsSummary />}
                {Array.from({ length: rows }).map((_, i) => (
                    <Box key={i}>
                        <Row />
                        {i < rows - 1 ? <Divider /> : null}
                    </Box>
                ))}
            </Box>
        </SubtleShimmer>
    )
}

export default memo(ListSkeleton)
